export interface SEOData {
  title?: string
  description?: string
  keywords?: string[]
  image?: string
  url?: string
  type?: 'website' | 'article' | 'profile'
  publishedTime?: string
  modifiedTime?: string
  author?: string
  section?: string
  tags?: string[]
}

export interface StructuredData {
  '@context': string
  '@type': string
  [key: string]: any
}

/**
 * Generate meta tags for SEO
 */
export function generateMetaTags(data: SEOData): string {
  const {
    title = import.meta.env.VITE_DEFAULT_TITLE || 'HammerMUD',
    description = import.meta.env.VITE_DEFAULT_DESCRIPTION || 'Modern MUD Gaming Experience',
    keywords = [],
    image,
    url = import.meta.env.VITE_SITE_URL || 'https://hammermud.com',
    type = 'website',
    publishedTime,
    modifiedTime,
    author,
    section,
    tags = []
  } = data

  const metaTags: string[] = []

  // Basic meta tags
  metaTags.push(`<title>${title}</title>`)
  metaTags.push(`<meta name="description" content="${description}" />`)
  
  if (keywords.length > 0) {
    metaTags.push(`<meta name="keywords" content="${keywords.join(', ')}" />`)
  }

  if (author) {
    metaTags.push(`<meta name="author" content="${author}" />`)
  }

  // Open Graph tags
  metaTags.push(`<meta property="og:type" content="${type}" />`)
  metaTags.push(`<meta property="og:title" content="${title}" />`)
  metaTags.push(`<meta property="og:description" content="${description}" />`)
  metaTags.push(`<meta property="og:url" content="${url}" />`)
  metaTags.push(`<meta property="og:site_name" content="${import.meta.env.VITE_SITE_NAME || 'HammerMUD'}" />`)

  if (image) {
    metaTags.push(`<meta property="og:image" content="${image}" />`)
    metaTags.push(`<meta property="og:image:alt" content="${title}" />`)
  }

  if (publishedTime) {
    metaTags.push(`<meta property="article:published_time" content="${publishedTime}" />`)
  }

  if (modifiedTime) {
    metaTags.push(`<meta property="article:modified_time" content="${modifiedTime}" />`)
  }

  if (author) {
    metaTags.push(`<meta property="article:author" content="${author}" />`)
  }

  if (section) {
    metaTags.push(`<meta property="article:section" content="${section}" />`)
  }

  tags.forEach(tag => {
    metaTags.push(`<meta property="article:tag" content="${tag}" />`)
  })

  // Twitter Card tags
  metaTags.push(`<meta name="twitter:card" content="summary_large_image" />`)
  metaTags.push(`<meta name="twitter:title" content="${title}" />`)
  metaTags.push(`<meta name="twitter:description" content="${description}" />`)

  if (image) {
    metaTags.push(`<meta name="twitter:image" content="${image}" />`)
  }

  if (import.meta.env.VITE_TWITTER_HANDLE) {
    metaTags.push(`<meta name="twitter:site" content="${import.meta.env.VITE_TWITTER_HANDLE}" />`)
  }

  // Additional meta tags
  metaTags.push(`<meta name="robots" content="index, follow" />`)
  metaTags.push(`<link rel="canonical" href="${url}" />`)

  return metaTags.join('\n')
}

/**
 * Generate structured data (JSON-LD)
 */
export function generateStructuredData(data: SEOData): StructuredData[] {
  const structuredData: StructuredData[] = []

  // Website structured data
  structuredData.push({
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: import.meta.env.VITE_SITE_NAME || 'HammerMUD',
    description: import.meta.env.VITE_SITE_DESCRIPTION || 'Modern MUD Gaming Experience',
    url: import.meta.env.VITE_SITE_URL || 'https://hammermud.com'
  })

  // Article structured data (if applicable)
  if (data.type === 'article' && data.title) {
    const article: StructuredData = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: data.title,
      description: data.description || '',
      url: data.url || '',
    }

    if (data.author) {
      article.author = {
        '@type': 'Person',
        name: data.author
      }
    }

    if (data.publishedTime) {
      article.datePublished = data.publishedTime
    }

    if (data.modifiedTime) {
      article.dateModified = data.modifiedTime
    }

    if (data.image) {
      article.image = data.image
    }

    structuredData.push(article)
  }

  return structuredData
}

/**
 * Generate sitemap entry
 */
export function generateSitemapEntry(url: string, lastmod?: string, changefreq?: string, priority?: number): string {
  const entry = [`  <url>`, `    <loc>${url}</loc>`]
  
  if (lastmod) {
    entry.push(`    <lastmod>${lastmod}</lastmod>`)
  }
  
  if (changefreq) {
    entry.push(`    <changefreq>${changefreq}</changefreq>`)
  }
  
  if (priority !== undefined) {
    entry.push(`    <priority>${priority}</priority>`)
  }
  
  entry.push(`  </url>`)
  
  return entry.join('\n')
}

/**
 * Generate complete sitemap XML
 */
export function generateSitemap(urls: Array<{
  url: string
  lastmod?: string
  changefreq?: string
  priority?: number
}>): string {
  const header = `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">`

  const footer = `</urlset>`

  const entries = urls.map(entry => generateSitemapEntry(entry.url, entry.lastmod, entry.changefreq, entry.priority))

  return [header, ...entries, footer].join('\n')
}

/**
 * Validate SEO data
 */
export function validateSEO(data: SEOData): { isValid: boolean; errors: string[] } {
  const errors: string[] = []

  if (!data.title) {
    errors.push('Title is required')
  } else if (data.title.length > 60) {
    errors.push('Title should be 60 characters or less')
  }

  if (!data.description) {
    errors.push('Description is required')
  } else if (data.description.length > 160) {
    errors.push('Description should be 160 characters or less')
  }

  if (data.keywords && data.keywords.length > 10) {
    errors.push('Keywords should be limited to 10 or fewer')
  }

  return {
    isValid: errors.length === 0,
    errors
  }
}